// PlanSelection — one component, two mounts (D-02, 50-CONTEXT.md).
//
// chrome="standalone" renders at the public /signup route (src/App.js);
// chrome="embedded" replaces the zero-workspace "Create your first team"
// block inside src/components/AppRouter.js. The prop controls ONLY the
// outer wrapper and page-level chrome (logo, headline, footer) — the
// interval toggle, the tier grid, the copy, and every interaction below are
// identical in both mounts. That identity is D-02's whole reason for
// existing: a duplicated surface drifts, which is exactly the failure
// src/config/publicRoutes.js's own header comment documents (a duplicated
// route list that silently omitted /portal/:token).
//
// Neither mount adds a height/overflow wrapper INSIDE this component. The
// standalone route element (src/App.js) supplies its own definite-height,
// self-scrolling wrapper; the embedded mount inherits a correct scroll
// container for free from App.js's `mainClassName`. Adding a second
// scrolling wrapper in here would nest two scroll regions.
//
// Plan 50-11 adds the three states that sit behind this grid:
//   - the post-Checkout "setting up your workspace" wait state, driven by an
//     onSnapshot listener over userAccount/{uid} (D-08) -- entitlement comes
//     only from that server write, never from the Checkout redirect
//     (SIGNUP-04);
//   - the provisioning-failure state the wait state hands off to at 45s
//     (D-12), carrying the Checkout session id and a working "Try again";
//   - the Free path's own button-local states, still to land in this plan's
//     next task.
// See each state's own comment below for the reasoning specific to it.

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Copy } from 'lucide-react';
import { onSnapshot, doc } from 'firebase/firestore';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import Button from '../../ui/primitives/Button';
import Card from '../../ui/primitives/Card';
import IconButton from '../../ui/primitives/IconButton';
import Spinner from '../../ui/primitives/Spinner';
import { usePendingAction } from '../../../hooks/usePendingAction';
import { useRedirectHold } from '../../../hooks/useRedirectHold';
import TierCard from './TierCard';
import PromoCodeField, { PromoRefusal } from './PromoCodeField';
import usePromoCode from '../../../hooks/usePromoCode';
import { sendablePromo, tierPromoDisplay, promoReasonFromError, promoErrorMessage } from '../../../utils/promoCode';
import signupTiersSnapshot from '../../../signupTiersSnapshot.json';
import { PUBLIC_ROUTE_PATHS } from '../../../config/publicRoutes';
import { SUPPORT_EMAIL } from '../../../config/support';
import { ensureUserProfileDocument } from '../../../services/userProfile';
import { db, auth } from '../../../firebase';
import { getPublicSignupTiers, createCheckoutSession, createFreeWorkspace } from '../../../services/signupService';
// ensureUserAccount already has a wrapper in invitationService.js (D-10's
// read-only callable, extended by plan 50-08 with the zero-workspace Stripe
// reconcile); the failure state's "Try again" below reuses that import
// rather than adding a second, divergent wrapper for the same callable in
// signupService.js.
import { ensureUserAccount } from '../../../services/invitationService';

// Wait-state timing (D-12, Claude's discretion per UI-SPEC.md's timing
// table -- defaults, not locked). Stripe webhook delivery and the
// provisionWorkspace transaction are both normally sub-5-second operations
// (Phase 49's live-verified webhook), so 45s is generous headroom before
// assuming the immediate-delivery path failed, without leaving a paying
// customer at a spinner for minutes. This is well short of D-13's
// hours/days-long retry horizon, which operates after the user has already
// left this screen.
const WAIT_STAGE2_MS = 15000;
const WAIT_FAILURE_MS = 45000;

// ─── Tier cache (stale-while-revalidate) ──────────────────────────────────
//
// getPublicSignupTiers is warm in ~60-110ms but this project runs no warm
// instances, so a visitor arriving at a cold function watched a skeleton for
// seconds. The catalog is public, identical for every visitor, and changes
// about never, so it is cached in localStorage and rendered IMMEDIATELY on a
// return visit while a fetch revalidates in the background.
//
// WHY A STALE PRICE CANNOT MISCHARGE ANYONE. The card is a display surface,
// not a source of truth: the CTA sends only a slug and an interval, and
// createCheckoutSession re-resolves the tier and the price server-side and
// refuses anything not publicly available (SIGNUP-02). A stale figure can
// therefore be briefly wrong on screen, and is corrected the moment the fetch
// lands — but it can never become the amount charged, and Stripe's own hosted
// page shows the real price before any card is entered.
//
// The version suffix is part of the key, so a change to the projected shape
// orphans old entries rather than reading them back as a valid cache.
const TIERS_CACHE_KEY = 'kf.signup.tiers.v1';
// A ceiling, not the refresh interval — every mount revalidates regardless.
// This only bounds how stale a first paint may be for someone returning after
// a long absence.
const TIERS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// The catalog baked into the bundle at generation time
// (scripts/generateSignupTiersSnapshot.js). This is what a FIRST-TIME visitor
// paints -- they have no localStorage cache by definition, and they are
// exactly who this page exists for. Regenerating it is a committed, reviewable
// diff rather than a live fetch during the build, so `npm run build:agent`
// stays hermetic on a clean CI checkout with no credentials.
//
// Like the cache, this is a display seed and never a source of truth: the
// mount below revalidates against the live callable regardless, and the CTA
// sends only a slug and an interval for the server to re-resolve.
// Both signup callables are authenticated: createCheckoutSession needs a uid to
// reserve a workspace id and to put `uid` in the Checkout Session metadata the
// webhook reads, and createFreeWorkspace provisions for the caller. But /signup
// is a PUBLIC route -- a stranger is the whole point of it -- so a signed-out
// visitor clicking a CTA used to hit a 401 "Sign in required." with no
// explanation and no way forward.
//
// The page's own subhead already promises the right flow ("Pick a plan and sign
// in with Google."): choose first, authenticate second. This is that second
// step. Popup rather than redirect, matching App.js's signInWithGoogle, so the
// chosen plan and interval survive in component state instead of having to be
// round-tripped through the URL.
//
// Called synchronously from the click handler before any await, so the browser
// still attributes the popup to a user gesture.
async function ensureSignedIn() {
    if (auth.currentUser) return auth.currentUser;
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    // Seed users/{uid}, exactly as App.js's signInWithGoogle does. This is the
    // app's SECOND sign-in entrance and the document is the client's to write
    // -- ensureUserAccount deliberately does not. Skipping it leaves a paying
    // customer with a workspace and a billable tier but no profile row, which
    // makes them invisible in admin's UsageAccountsView (it iterates `users`
    // and joins workspaces onto it) with nothing anywhere reporting an error.
    //
    // Awaited, so a signup cannot proceed to Checkout on a half-made account.
    await ensureUserProfileDocument(result.user);
    return result.user;
}

// Sign-in failures a visitor can actually hit, in their own words. The last one
// is the reason 50-PREFLIGHT.md records `disabledUserSignup` for both projects:
// with that flag on, EVERY first sign-in fails this way, and without a specific
// message it reads as a bug in this phase's code rather than a project setting.
function signInErrorMessage(code) {
    if (code.endsWith('popup-closed-by-user') || code.endsWith('cancelled-popup-request')) {
        return 'Sign-in was cancelled. Choose a plan again when you are ready.';
    }
    if (code.endsWith('popup-blocked')) {
        return 'Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.';
    }
    if (code.endsWith('admin-restricted-operation')) {
        return `New accounts are not open yet. Email ${SUPPORT_EMAIL} and we will set you up.`;
    }
    if (code.endsWith('network-request-failed')) {
        return 'We could not reach Google to sign you in. Check your connection and try again.';
    }
    return `We could not sign you in. Try again, or email ${SUPPORT_EMAIL} if it keeps happening.`;
}

function readSnapshotTiers() {
    try {
        const tiers = signupTiersSnapshot?.tiers;
        if (!Array.isArray(tiers) || tiers.length === 0) return null;
        const usable = tiers.every(
            (t) => t && typeof t.slug === 'string' && typeof t.name === 'string'
        );
        return usable ? tiers : null;
    } catch {
        return null;
    }
}

function readCachedTiers() {
    // Every access is wrapped: localStorage throws outright in some privacy
    // modes rather than returning null, and a corrupt entry must degrade to
    // "no cache", never to a crash on the signup page.
    try {
        const raw = window.localStorage.getItem(TIERS_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.tiers) || typeof parsed.cachedAt !== 'number') return null;
        if (Date.now() - parsed.cachedAt > TIERS_CACHE_MAX_AGE_MS) return null;
        // Shape-check every entry. A half-written or hand-edited value must
        // not reach the grid, which would render "undefined" at full size.
        const usable = parsed.tiers.length > 0 && parsed.tiers.every(
            (t) => t && typeof t.slug === 'string' && typeof t.name === 'string'
        );
        return usable ? parsed.tiers : null;
    } catch {
        return null;
    }
}

function writeCachedTiers(tiers) {
    try {
        window.localStorage.setItem(
            TIERS_CACHE_KEY,
            JSON.stringify({ cachedAt: Date.now(), tiers })
        );
    } catch {
        // A full or unavailable localStorage is not worth surfacing — the
        // page works exactly as it did before caching existed.
    }
}

function TierSkeleton() {
    return (
        <Card padding="lg" className="w-full flex flex-col h-full animate-pulse-strong">
            <div className="h-3 w-20 rounded bg-[color:var(--surface-subtle)]" />
            <div className="mt-4 h-8 w-24 rounded bg-[color:var(--surface-subtle)]" />
            <div className="mt-3 h-4 w-full rounded bg-[color:var(--surface-subtle)]" />
            <div className="mt-6 space-y-2">
                <div className="h-3 w-full rounded bg-[color:var(--surface-subtle)]" />
                <div className="h-3 w-5/6 rounded bg-[color:var(--surface-subtle)]" />
                <div className="h-3 w-4/6 rounded bg-[color:var(--surface-subtle)]" />
            </div>
            <div className="mt-auto pt-6 h-11 w-full rounded-lg bg-[color:var(--surface-subtle)]" />
        </Card>
    );
}

// The post-Checkout wait state (D-08, D-12, SIGNUP-04) -- the highest-risk
// surface in this phase. Renders only while sessionId (captured on mount
// from the Checkout redirect) is present; navigation happens elsewhere, in
// PlanSelection's own userAccount/{uid} listener effect, never here.
// Returning from Checkout with no signed-in user.
//
// The wait and failure states both rest on the userAccount/{uid} listener, and
// that listener cannot start without a uid -- so a signed-out visitor here gets
// a spinner that can never resolve, then a failure screen telling them their
// payment succeeded, from an app that does not know who they are. Every symptom
// is silent: no listener to error, so no error to report, so nothing to
// disclose. It is the worst version of this screen, and it is reachable
// whenever the session does not survive the Stripe round trip (a different
// browser, a cleared or partitioned cookie jar, a link opened elsewhere).
//
// SIGNUP-04 still applies: this names no tier and no plan, and claims nothing
// about the payment, because without a uid nothing here can be verified.
function SignInToFinishState({ sessionId, onSignIn, signingIn, error }) {
    return (
        <div className="flex flex-col items-center justify-center text-center py-16">
            <p className="kf-h2">Sign in to finish setting up.</p>
            <p className="kf-sub mt-2 max-w-[420px]">
                You're signed out on this device, so we can't match this to your account.
                Sign in with the same Google account you used at checkout and we'll pick up
                where you left off.
            </p>
            <div className="mt-6">
                <Button variant="accent" size="lg" loading={signingIn} loadingLabel="Signing in…" onClick={onSignIn}>
                    Sign in with Google
                </Button>
            </div>
            {error && (
                <p role="alert" className="text-sm mt-3" style={{ color: 'var(--color-danger)' }}>
                    {error}
                </p>
            )}
            {sessionId && (
                <p className="kf-sub mt-6 text-xs">Session: {sessionId}</p>
            )}
        </div>
    );
}

function WaitState({ waitPhase, listenerFailed }) {
    return (
        <div className="flex flex-col items-center justify-center text-center py-16">
            <span style={{ color: 'var(--color-accent)' }}>
                <Spinner size="md" className="!h-10 !w-10" />
            </span>
            <p className="kf-h2 mt-6">Setting up your workspace…</p>
            <p className="kf-sub mt-2 max-w-[420px]">
                {waitPhase === 'stage2'
                    ? 'Still working. This is taking longer than usual, but nothing is lost.'
                    : 'This usually takes a few seconds.'}
            </p>
            {listenerFailed && (
                /* This screen's entire promise is that it will move on its own
                   when the workspace appears. The listener IS that promise, so
                   if it has died the spinner is telling the customer something
                   untrue for as long as they sit here. Say so instead. The copy
                   still names no tier or plan (SIGNUP-04). */
                <p role="status" className="kf-sub mt-3 max-w-[420px]">
                    We've lost our live connection, so this page won't update on its own.
                    Reload once your workspace is ready.
                </p>
            )}
            {/*
              SIGNUP-04 / T-50-62 (Never Shown, per the Copywriting
              Contract): no tier name, no plan name, and no "You're on the
              X plan" confirmation appears anywhere in this render path, at
              any stage. Entitlement is sourced only from the
              userAccount/{uid} listener's own write -- never from the
              Checkout redirect this component mounted from -- and the
              screen names no tier until the app has actually navigated
              into the real workspace, where usageAssignments is the true
              source. "You're on the Pro plan" is exactly the reassuring
              line someone will want to add here later; it would be false
              until the listener resolves.
            */}
        </div>
    );
}

// The provisioning-failure state (D-12, D-14, SIGNUP-05) -- the single most
// important copy in this phase, because the person reading it has already
// been charged. Same centered container as WaitState above (no layout
// shift on the 15s->45s handoff), never reachable from the Free path
// (nothing was charged there, so "your payment went through" would be
// false), and rendered with the userAccount listener still mounted above
// it in PlanSelection -- see that effect's own comment for why unmounting
// it here would quietly break SIGNUP-05's cheapest recovery path.
function FailureState({ sessionId, onRetry, retrying, retryError, listenerFailed }) {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = React.useCallback(() => {
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
        navigator.clipboard.writeText(sessionId).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            // Clipboard access can be denied by the browser; the session id
            // is still visible and selectable as plain text, so this is not
            // a dead end -- just no toast for it.
        });
    }, [sessionId]);

    // SUPPORT_EMAIL, not a hardcoded address: src/config/support.js is "the one
    // place the customer-facing support address lives", and its own comment
    // names this exact situation -- the moments a customer most needs a human.
    // A charge that went through while provisioning did not is one of them.
    const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Workspace setup failed (session ${sessionId})`)}`;

    return (
        <div className="flex flex-col items-center text-center py-16">
            <div className="icon-chip bg-[color:var(--surface-danger)] mb-5">
                <AlertCircle size={20} style={{ color: 'var(--color-danger)' }} aria-hidden="true" />
            </div>
            {/* Exact text from the Copywriting Contract -- this is the copy,
                not an example. No softening padding, no hedge, per
                VOICE.md §1's "read like someone who has read a purchase
                agreement" standard. States, in order: what happened, that
                the money is accounted for and needs no action, a
                searchable reference, and two paths forward. */}
            <p className="kf-h2">Your payment went through. Setting up your workspace didn't.</p>
            <p className="kf-sub mt-3 max-w-[440px]">
                The charge is real, and it's already accounted for — there's nothing you need to do about the money. Getting your workspace ready is what failed, and we can see it happened.
            </p>
            <div className="mono text-sm mt-4 flex items-center gap-2 text-[color:var(--text-muted)]">
                <span>Session: {sessionId}</span>
                <IconButton aria-label="Copy session ID" onClick={handleCopy}>
                    <Copy size={14} aria-hidden="true" />
                </IconButton>
                {copied && (
                    <span className="text-xs" style={{ color: 'var(--color-primary)' }}>Copied</span>
                )}
            </div>
            <div className="mt-6 flex items-center gap-3">
                <Button variant="accent" size="md" loading={retrying} loadingLabel="Trying again…" onClick={onRetry}>
                    Try again
                </Button>
                <Button as="a" href={mailtoHref} variant="ghost" size="md">
                    Email support
                </Button>
            </div>
            {retryError && (
                <p role="alert" className="text-sm mt-3" style={{ color: 'var(--color-danger)' }}>
                    {retryError}
                </p>
            )}
            {listenerFailed && (
                /* This screen otherwise implies it is still watching and will
                   move on its own. With the listener dead that is untrue, and
                   leaving it unsaid means the customer waits on something that
                   is never coming. */
                <p role="status" className="kf-sub mt-4">
                    We've lost our live connection, so this page won't update on its own.
                    Use Try again once your workspace is ready.
                </p>
            )}
        </div>
    );
}

function PlanSelection({ chrome }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Annual is the default (it is the better deal and the one we lead with);
    // ?interval=monthly is the only thing that selects monthly, so a deep link
    // back from a cancelled Checkout still restores what the visitor picked.
    const initialInterval = searchParams.get('interval') === 'monthly' ? 'monthly' : 'yearly';
    const planHint = searchParams.get('plan') || null;
    // ?promo= from an event page or a cancelled Checkout (docs/promo-codes.md).
    // Read once on mount by the hook. Sign-in is a popup, so the code survives it.
    const promoCode = usePromoCode(searchParams.get('promo'));
    // Set when checkout refused the code: { tier, message }.
    const [promoRefusal, setPromoRefusal] = React.useState(null);
    // Captured ONCE on mount, not read live from searchParams on every
    // render -- the failure state built in this plan's next task still
    // needs it after later renders, and it is the only thing support can
    // search on. `session_id` arriving on this URL is what Stripe's
    // success_url (plan 50-06) carries; its mere presence is what puts this
    // mount into the wait journey rather than the plain grid, decoupled
    // from whichever route actually matched (chrome="standalone" or
    // chrome="embedded" both read the same URL).
    const [sessionId] = React.useState(() => searchParams.get('session_id'));
    // 'grid' | 'wait' | 'failure'. Starts on 'wait' whenever a session_id
    // arrived on this mount's URL (D-12) -- optimistically starting on
    // 'grid' and only later noticing the redirect would itself be a flash
    // of the wrong screen, and SIGNUP-04 already forbids trusting anything
    // ABOUT the redirect beyond "a Checkout session exists to reference".
    const [stage, setStage] = React.useState(() => (searchParams.get('session_id') ? 'wait' : 'grid'));
    const [waitPhase, setWaitPhase] = React.useState('stage1');
    // "Try again" button-local spinner, distinct from `stage` -- the
    // failure screen must NOT reset to the wait state's stage 1 while a
    // retry is in flight (UI-SPEC.md §4).
    const [retryError, setRetryError] = React.useState(null);

    const [interval, setInterval_] = React.useState(initialInterval);
    // Seeds from cache so a return visitor sees real prices on first paint
    // instead of the skeleton. A cache miss keeps the old behavior exactly.
    const [loadState, setLoadState] = React.useState(() => {
        // Precedence: this visitor's own cached fetch, then the baked
        // snapshot, then the skeleton. The cache outranks the snapshot because
        // it came from a real response to THIS visitor within the last day,
        // whereas the snapshot is as old as the last generation.
        const cached = readCachedTiers();
        if (cached) return { status: 'loaded', tiers: cached, source: 'cache' };
        const snapshot = readSnapshotTiers();
        if (snapshot) return { status: 'loaded', tiers: snapshot, source: 'snapshot' };
        return { status: 'loading', tiers: [] };
    });
    // Grid-level error, distinct from a per-card inline error — set on a
    // failed-precondition/invalid-argument response from createCheckoutSession,
    // which means the request itself was invalid rather than something the
    // customer can retry by clicking the same button again.
    const [gridError, setGridError] = React.useState(null);
    // Set when a revalidation fails while seeded content (cache or snapshot)
    // is on screen. Without this the page would look perfectly healthy while
    // the backend is down, and the visitor would only discover that by
    // clicking a CTA that then fails -- worse than the skeleton it replaced.
    const [unconfirmed, setUnconfirmed] = React.useState(false);
    // Set when the userAccount listener errors. The wait and failure screens
    // both promise to move on their own; if the listener is dead they cannot
    // keep that promise, and must say so rather than wait forever.
    const [listenerFailed, setListenerFailed] = React.useState(false);
    // Sign-in from the return-from-Checkout prompt below.
    const [signInError, setSignInError] = React.useState(null);
    // Per-tier checkout submission state, keyed by slug — { status: 'idle' |
    // 'submitting' | 'error', inline, message }.
    const [checkoutState, setCheckoutState] = React.useState({});

    const loadTiers = React.useCallback(({ showSpinner = false } = {}) => {
        // Only an explicit retry clears the screen. A background revalidation
        // must never replace prices the visitor is already reading with a
        // skeleton — that would make the cache worse than no cache.
        if (showSpinner) setLoadState({ status: 'loading', tiers: [] });
        setGridError(null);
        setUnconfirmed(false);
        getPublicSignupTiers()
            .then((data) => {
                const tiers = Array.isArray(data?.tiers) ? data.tiers : [];
                setLoadState({ status: 'loaded', tiers, source: 'live' });
                setUnconfirmed(false);
                // Only cache a non-empty result. Caching an empty list would
                // pin the "Plans aren't available right now." state for a day.
                if (tiers.length > 0) writeCachedTiers(tiers);
            })
            .catch(() => {
                // A failed revalidation is not the same as having nothing to
                // show. If cached tiers are already on screen, keep them --
                // swapping working content for an error screen loses the whole
                // point of the cache. Only a cold failure surfaces the error.
                setLoadState((prev) => {
                    const hasContent = prev.status === 'loaded' && prev.tiers.length > 0;
                    // Keep real content on screen, but say plainly that it is
                    // unconfirmed. Only a failure with nothing to show at all
                    // falls through to the error state.
                    setUnconfirmed(hasContent);
                    return hasContent ? prev : { status: 'error', tiers: [] };
                });
            });
    }, []);

    React.useEffect(() => {
        loadTiers();
        // Runs once on mount only — the interval toggle re-renders price
        // figures client-side and must NOT refetch the tier list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resolves the signed-in uid directly against firebase/auth rather than
    // through src/contexts/AuthContext.js. PlanSelection mounts both as a
    // public, pre-shell route (chrome="standalone", outside any
    // <AuthProvider>) and inside the authenticated shell
    // (chrome="embedded"), and only the second is ever wrapped in one.
    // WelcomePage.js and AcceptInvitationPage.js resolve their own auth
    // state the same way, for the same reason.
    const [uid, setUid] = React.useState(null);
    // `null` uid means two very different things -- "auth has not hydrated yet"
    // and "nobody is signed in" -- and the return-from-Checkout path has to
    // tell them apart. Without this flag the sign-in prompt below would flash
    // on every load before Firebase restores the session.
    const [authResolved, setAuthResolved] = React.useState(false);
    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUid(firebaseUser?.uid || null);
            setAuthResolved(true);
        });
        return unsubscribe;
    }, []);

    // D-08: the client waits on a listener over userAccount/{uid}, watching
    // for primaryTeamWorkspaceId to appear -- never a poll (Phase 38 already
    // established why for exactly this kind of state), and no rules change
    // needed (firestore.rules:191-195 already lets a signed-in user `get`
    // their own userAccount document). Runs only once both sessionId and uid
    // are known, and -- this is the part a plausible-looking refactor most
    // easily breaks, addressed fully once this plan's failure state lands --
    // it is NOT scoped to the 'wait' stage, so it stays subscribed through a
    // later failure state too: a late primaryTeamWorkspaceId (Stripe's own
    // retry, or the admin repair action from plan 50-13) must navigate the
    // user in on its own, with no click (SIGNUP-05, T-50-66).
    React.useEffect(() => {
        if (!sessionId || !uid) return undefined;
        const unsubscribe = onSnapshot(
            doc(db, 'userAccount', uid),
            (snapshot) => {
                const data = snapshot.exists() ? snapshot.data() : null;
                if (data && data.primaryTeamWorkspaceId) {
                    navigate('/', { replace: true });
                }
            },
            (error) => {
                // This listener is the ONLY thing that carries a customer from
                // the wait or failure screen into their workspace with no click
                // (SIGNUP-05). Without an error callback, onSnapshot swallows a
                // permission, network, or transport failure entirely: the
                // screen sits there looking healthy while the one mechanism
                // that could end it is dead. Record it so the state below can
                // offer the manual way out, and report it -- a silent failure
                // here is indistinguishable from provisioning simply not having
                // happened yet, which is the wrong diagnosis every time.
                console.error('[signup] userAccount listener failed', error);
                setListenerFailed(true);
            }
        );
        return unsubscribe;
    }, [sessionId, uid, navigate]);

    // Bounded wait (D-12): stage-1 copy escalates to stage-2 at
    // WAIT_STAGE2_MS, and the wait state hands off to the failure state at
    // WAIT_FAILURE_MS. Scoped to the 'wait' stage so these timers do not
    // keep running once the failure state (or a navigation away) has
    // already happened.
    React.useEffect(() => {
        if (stage !== 'wait') return undefined;
        // Do not run the bounded wait while the visitor is being asked to sign
        // in: nothing is provisioning yet, so escalating to "taking longer than
        // usual" and then to the failure screen would narrate a failure that is
        // not happening.
        if (Boolean(sessionId) && authResolved && !uid) return undefined;
        const stage2Timer = setTimeout(() => setWaitPhase('stage2'), WAIT_STAGE2_MS);
        const failureTimer = setTimeout(() => setStage('failure'), WAIT_FAILURE_MS);
        return () => {
            clearTimeout(stage2Timer);
            clearTimeout(failureTimer);
        };
    }, [stage, sessionId, authResolved, uid]);

    // "Try again" (D-13 mechanism #2): re-invokes ensureUserAccount, which
    // plan 50-08 extended with the zero-workspace Stripe reconcile. Does
    // NOT reset `stage` back to 'wait' on failure -- the failure screen
    // re-renders unchanged, same session id, no new copy, per UI-SPEC.md §4.
    // On success it navigates directly from the response; the listener
    // above would also catch the same write independently, so this is a
    // belt-and-suspenders navigate, not the only path to it.
    // The checkout stays marked submitting through the Stripe redirect (the
    // redirect hold). When the hold drops (Back from Stripe restoring this page
    // from the bfcache, or a navigation that never happened), reset it so the
    // CTA works again (UI-WR-09).
    const releaseCheckoutHold = React.useCallback(() => {
        setCheckoutState((prev) => {
            const next = {};
            Object.keys(prev).forEach((slug) => {
                next[slug] = prev[slug]?.status === 'submitting' ? { status: 'idle' } : prev[slug];
            });
            return next;
        });
    }, []);
    const holdCheckout = useRedirectHold(releaseCheckoutHold);

    const retryAccount = React.useCallback(async () => {
        setRetryError(null);
        try {
            const data = await ensureUserAccount();
            if (data?.teamWorkspaceId) {
                navigate('/', { replace: true });
            }
        } catch {
            // The failure screen stays as it is (UI-SPEC section 4); say that
            // the retry itself failed rather than leaving the click unanswered.
            setRetryError("Couldn't set up your account. Try again.");
        }
    }, [navigate]);
    const { run: handleRetry, pending: retrying } = usePendingAction(retryAccount);

    // The signed-out return path's way back in. On success onAuthStateChanged
    // sets `uid`, which subscribes the userAccount listener -- so a workspace
    // that already exists (or arrives later, from a retry or an admin repair)
    // carries them straight in with no further clicks, exactly as if they had
    // been signed in the whole time.
    const signInToFinish = React.useCallback(async () => {
        setSignInError(null);
        try {
            await ensureSignedIn();
        } catch (error) {
            setSignInError(signInErrorMessage(error?.code || ''));
        }
    }, []);
    const { run: handleSignInToFinish, pending: signingIn } = usePendingAction(signInToFinish);

    const handleCheckout = React.useCallback(async (tier, { withoutPromo = false } = {}) => {
        // Show the button-local spinner for the sign-in leg too -- from the
        // visitor's side, authenticating IS part of "Start 14-day trial", and
        // an unresponsive button during a popup reads as a broken one.
        setGridError(null);
        setPromoRefusal(null);
        setCheckoutState((prev) => ({ ...prev, [tier.slug]: { status: 'submitting' } }));

        try {
            await ensureSignedIn();
        } catch (signInError) {
            setCheckoutState((prev) => ({
                ...prev,
                [tier.slug]: {
                    status: 'error',
                    inline: true,
                    message: signInErrorMessage(signInError?.code || ''),
                },
            }));
            return;
        }

        // NOTE: there is deliberately no ensureUserAccount pre-check here.
        //
        // An earlier version read it to decide whether the account already had
        // a workspace, which measured at ~520ms on deployed keyflowdev -- a
        // quarter of the ~2.1s a visitor waits between clicking the CTA and
        // seeing Stripe. It bought nothing: createCheckoutSession refuses a
        // provisioned account server-side with already-exists (T-50-61), and
        // the catch below turns that refusal into the same navigation the
        // pre-check would have made. A round trip whose only job is to predict
        // an answer the next round trip already gives is pure latency on the
        // product's front door.
        //
        // createFreeWorkspace refuses the same case the same way.

        if (tier.slug === 'free') {
            // D-09/D-11: no Stripe hop, no listener, no session id --
            // provisionWorkspace runs inside createFreeWorkspace's own
            // request and either succeeds or throws before the callable
            // returns, so its own response IS the answer (unlike the paid
            // path, where the redirect is deliberately never trusted per
            // SIGNUP-04). Reuses the same per-tier checkoutState map the
            // paid tiers use, keyed by 'free', purely for the button-local
            // loading/error UI -- never the full-screen wait or failure state.
            createFreeWorkspace()
                .then(() => {
                    navigate('/', { replace: true });
                })
                .catch((error) => {
                    const code = error?.code || '';
                    if (code.endsWith('already-exists')) {
                        // Already provisioned -- the account belongs in the app,
                        // not on a signup screen (T-50-61).
                        navigate('/', { replace: true });
                        return;
                    }
                    let message;
                    if (code.endsWith('resource-exhausted')) {
                        message = `You've reached the limit for new free workspaces from this account. Email ${SUPPORT_EMAIL} if this is a mistake.`;
                    } else if (code.endsWith('permission-denied')) {
                        message = `We can't create a workspace for this email domain. Email ${SUPPORT_EMAIL} if this is a mistake.`;
                    } else {
                        message = `Something went wrong creating your workspace. Try again, or email ${SUPPORT_EMAIL} if it keeps happening.`;
                    }
                    setCheckoutState((prev) => ({
                        ...prev,
                        free: { status: 'error', inline: true, message },
                    }));
                });
            return;
        }

        // The click's render can predate the code's lookup settling (a cold
        // getPromoCode), so read the state it settles on, not this closure's.
        const latest = await promoCode.settled();
        const promo = withoutPromo ? null : sendablePromo(latest, tier.slug, interval);
        createCheckoutSession({ slug: tier.slug, interval, promo })
            .then((data) => {
                if (data?.url) {
                    // Stay disabled through the redirect — the whole mitigation
                    // for a double-click minting a second reserved id and a
                    // second Stripe session (T-50-52).
                    window.location.href = data.url;
                    holdCheckout();
                    return;
                }
                setCheckoutState((prev) => ({
                    ...prev,
                    [tier.slug]: { status: 'idle' },
                }));
            })
            .catch((error) => {
                const code = error?.code || '';
                const promoReason = promoReasonFromError(error);
                if (promoReason) {
                    setCheckoutState((prev) => ({ ...prev, [tier.slug]: { status: 'idle' } }));
                    setPromoRefusal({ tier, message: promoErrorMessage(promoReason, latest.promo, promo) });
                    return;
                }
                if (code.endsWith('already-exists')) {
                    // The server-side T-50-61 guard fired -- almost always
                    // because the account was provisioned in another tab or
                    // between this click and the check above.
                    navigate('/', { replace: true });
                    return;
                }
                if (code.endsWith('resource-exhausted') || code.endsWith('permission-denied')) {
                    setCheckoutState((prev) => ({
                        ...prev,
                        [tier.slug]: {
                            status: 'error',
                            inline: true,
                            message: error?.message || 'Something went wrong. Try again.',
                        },
                    }));
                } else {
                    // failed-precondition, invalid-argument, and anything else
                    // unrecognized render as a grid-level error rather than a
                    // per-card one.
                    setCheckoutState((prev) => ({ ...prev, [tier.slug]: { status: 'idle' } }));
                    setGridError(error?.message || 'Something went wrong. Try again.');
                }
            });
    }, [interval, navigate, promoCode, holdCheckout]);

    const grid = (
        <>
            <div className="flex justify-center mb-8">
                {/* .seg is an existing loaded class (index.css) whose descendant
                    rules target the rendered button element that Button already
                    emits — its own utility classes still apply for everything
                    .seg does not touch (focus ring, transitions, disabled state). */}
                {/* .seg's own `button.active` rule only swaps the background to
                    --surface-subtle, which reads as almost nothing next to an
                    unselected button. .seg is shared with the list filter chips,
                    so it is not safe to restyle globally; the selected state is
                    set inline here instead. Inline style also wins on
                    specificity over `.seg button.active` without !important. */}
                <div className="seg" role="group" aria-label="Billing interval">
                    {[
                        { value: 'yearly', label: 'Annual' },
                        { value: 'monthly', label: 'Monthly' },
                    ].map(({ value, label }) => {
                        const selected = interval === value;
                        return (
                            <Button
                                key={value}
                                variant="ghost"
                                className="kf-eyebrow border-0"
                                aria-pressed={selected}
                                style={selected
                                    ? {
                                        background: 'var(--color-primary)',
                                        color: 'var(--on-primary)',
                                        fontWeight: 600,
                                    }
                                    : undefined}
                                onClick={() => setInterval_(value)}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </div>
            </div>

            {promoRefusal ? (
                <PromoRefusal
                    message={promoRefusal.message}
                    onContinue={() => {
                        const { tier } = promoRefusal;
                        promoCode.remove();
                        handleCheckout(tier, { withoutPromo: true });
                    }}
                />
            ) : (
                <PromoCodeField promoCode={promoCode} interval={interval} />
            )}

            {loadState.status === 'loading' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
                    <TierSkeleton />
                    <TierSkeleton />
                    <TierSkeleton />
                    <TierSkeleton />
                </div>
            )}

            {loadState.status === 'error' && (
                <div className="text-center py-10">
                    <AlertCircle size={28} style={{ color: 'var(--color-danger)' }} className="mx-auto mb-3" aria-hidden="true" />
                    <p className="kf-h2">Couldn't load plans.</p>
                    <p className="kf-sub mt-1">Check your connection and try again.</p>
                    <Button
                        variant="secondary"
                        size="md"
                        className="mt-5"
                        onClick={() => loadTiers({ showSpinner: true })}
                    >
                        Retry loading plans
                    </Button>
                </div>
            )}

            {loadState.status === 'loaded' && loadState.tiers.length === 0 && (
                <div className="text-center py-10">
                    <p className="kf-h2">Plans aren't available right now.</p>
                    <p className="kf-sub mt-1">
                        This is on us, not your connection. Email {SUPPORT_EMAIL} and we'll get you set up by hand.
                    </p>
                </div>
            )}

            {loadState.status === 'loaded' && loadState.tiers.length > 0 && (
                <>
                    {gridError && (
                        <p role="alert" className="text-center text-sm mb-5" style={{ color: 'var(--color-danger)' }}>
                            {gridError}
                        </p>
                    )}
                    {unconfirmed && !gridError && (
                        <p role="status" className="kf-sub text-center mb-5">
                            Showing saved pricing — we couldn't reach the server just now.
                        </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
                        {loadState.tiers.map((tier) => (
                            <TierCard
                                key={tier.slug}
                                tier={tier}
                                interval={interval}
                                planHint={planHint}
                                checkoutState={checkoutState}
                                onCheckout={handleCheckout}
                                promo={tierPromoDisplay(promoCode.promo, tier.slug, interval)}
                            />
                        ))}
                    </div>
                </>
            )}
        </>
    );

    // The wait and failure states are IDENTICAL in both mounts (UI-SPEC.md
    // §1's contract table) -- only the grid gets mount-specific chrome
    // (logo/headline/footer for standalone, the section heading for
    // embedded). Swapping stage in one variable, rather than three
    // near-duplicate branches below, is what keeps the 15s->45s handoff a
    // content swap inside the same container rather than a remount.
    // The wait and failure states are BOTH unreachable-by-design without a uid:
    // each rests on the userAccount/{uid} listener, which cannot subscribe
    // without one. Rendering either to a signed-out visitor produces a screen
    // that can never resolve and, worse, asserts that a payment succeeded on
    // behalf of an account the app cannot identify. Gate both.
    const needsSignIn = Boolean(sessionId) && authResolved && !uid;
    const stageContent = stage === 'grid'
        ? grid
        : needsSignIn
            ? (
                <SignInToFinishState
                    sessionId={sessionId}
                    onSignIn={handleSignInToFinish}
                    signingIn={signingIn}
                    error={signInError}
                />
            )
            : stage === 'wait'
                ? <WaitState waitPhase={waitPhase} listenerFailed={listenerFailed} />
                : <FailureState sessionId={sessionId} onRetry={handleRetry} retrying={retrying} retryError={retryError} listenerFailed={listenerFailed} />;

    if (chrome === 'standalone') {
        return (
            <div className={`${stage === 'grid' ? 'max-w-[1180px]' : 'max-w-[640px]'} mx-auto pt-12 px-6 pb-16`}>
                <div className="text-center mb-10">
                    <img
                        src="/images/keyflow-logo.generated.svg"
                        alt="KeyFlow"
                        className="h-10 mx-auto mb-6"
                    />
                    {stage === 'grid' && (
                        <>
                            <h1 className="kf-h1">Choose your plan</h1>
                            <p className="kf-sub mt-2">Pick a plan and sign in with Google. No sales call.</p>
                        </>
                    )}
                </div>
                {stageContent}
                {stage === 'grid' && (
                    <div className="mt-16 text-center text-sm text-[color:var(--text-muted)]">
                        <a href={PUBLIC_ROUTE_PATHS.privacy} className="underline hover:opacity-80">Privacy Policy</a>
                        <span className="mx-2">|</span>
                        <a href={PUBLIC_ROUTE_PATHS.terms} className="underline hover:opacity-80">Terms of Service</a>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {stage === 'grid' && <h2 className="kf-h2 mb-6">Choose a plan to get started.</h2>}
            {stageContent}
        </div>
    );
}

export default PlanSelection;
